import systemService from '../../services/system';

const state = {
  configuration: {
    app_name: 'FlowIt',
    primary_color: '#1976D2',
    secondary_color: '#FFFFFF',
    logo_url: '/logo_flowit.png',
    logo_text_url: '/logo_flowit_text.png',
    favicon_url: '/favicon.ico',
    pwa: {
      name: 'FlowIt',
      short_name: 'FlowIt',
      description: 'FlowIt - Sistema de Gestão',
      theme_color: '#1976D2',
      background_color: '#FFFFFF'
    }
  },
  loading: true, // Começar com loading = true para evitar flash
  error: null,
  lastForceUpdate: null // Para rate limiting das atualizações forçadas
};

const mutations = {
  SET_CONFIGURATION(state, configuration) {
    state.configuration = configuration;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_LAST_FORCE_UPDATE(state, timestamp) {
    state.lastForceUpdate = timestamp;
  }
};

const actions = {
  async fetchSystemConfiguration({ commit, state }) {
    // Se já foi carregada com dados reais (não padrão) e não está carregando, não recarregar
    if (state.configuration && 
        state.configuration.app_name && 
        state.configuration.app_name !== 'FlowIt' && // Não é configuração padrão
        state.configuration.logo_url && 
        !state.loading) {
      return;
    }
    
    // Evitar múltiplas requisições simultâneas
    if (state.loading && state.configuration.app_name !== 'FlowIt') {
      return;
    }
    
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const configuration = await systemService.getSystemConfiguration();
      commit('SET_CONFIGURATION', configuration);
      
      // Aplicar configurações imediatamente
      
      // Atualizar variáveis CSS imediatamente
      if (configuration.primary_color) {
        document.documentElement.style.setProperty('--primary-color', configuration.primary_color);
        document.documentElement.style.setProperty('--primary-color-dark', darkenColor(configuration.primary_color, 15));
        document.documentElement.style.setProperty('--primary-color-light', lightenColor(configuration.primary_color, 15));
      }
      
      if (configuration.secondary_color) {
        document.documentElement.style.setProperty('--secondary-color', configuration.secondary_color);
      }
      
      // Adicionar outras variáveis de cor padrão
      document.documentElement.style.setProperty('--success-color', '#28a745');
      document.documentElement.style.setProperty('--warning-color', '#ffc107');
      document.documentElement.style.setProperty('--danger-color', '#dc3545');
      document.documentElement.style.setProperty('--info-color', '#17a2b8');
      document.documentElement.style.setProperty('--light-color', '#f8f9fa');
      document.documentElement.style.setProperty('--dark-color', '#343a40');
      document.documentElement.style.setProperty('--text-muted', '#666');
      document.documentElement.style.setProperty('--border-color', '#e0e0e0');
      
      // Update document title and favicon
      document.title = configuration.app_name;
      
      // Update favicon - handle multiple favicon link types
      const faviconSelectors = [
        'link[rel="icon"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]'
      ];
      
      faviconSelectors.forEach(selector => {
        let link = document.querySelector(selector);
        if (link) {
          link.href = configuration.favicon_url;
        }
      });
      
      // Ensure at least one favicon link exists
      if (!document.querySelector('link[rel="icon"]')) {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = configuration.favicon_url;
        document.head.appendChild(favicon);
      }
      
      // Update theme colors
      const themeColorMeta = document.querySelector('meta[name="theme-color"]') || document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      themeColorMeta.content = configuration.pwa.theme_color;
      if (!document.querySelector('meta[name="theme-color"]')) {
        document.head.appendChild(themeColorMeta);
      }
      
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao carregar configuração do sistema');
      console.error('Error in fetchSystemConfiguration:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Alias para compatibilidade
  async getSystemConfiguration({ dispatch }) {
    return await dispatch('fetchSystemConfiguration');
  },
  
  // Força atualização ignorando cache
  async forceUpdateSystemConfiguration({ commit, state }) {
    // Evitar muitas atualizações forçadas em pouco tempo (rate limiting)
    const now = Date.now();
    if (state.lastForceUpdate && (now - state.lastForceUpdate) < 2000) { // 2 segundos
      console.log('System Store: Ignorando força atualização (rate limit)');
      return;
    }
    
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    commit('SET_LAST_FORCE_UPDATE', now);
    
    try {
      // Adicionar cache busting no cliente também
      const cacheBuster = `?cb=${now}`;
      const configuration = await systemService.getSystemConfiguration(cacheBuster);
      commit('SET_CONFIGURATION', configuration);
      
      // Aplicar configurações imediatamente
      
      // Atualizar variáveis CSS imediatamente
      if (configuration.primary_color) {
        document.documentElement.style.setProperty('--primary-color', configuration.primary_color);
        document.documentElement.style.setProperty('--primary-color-dark', darkenColor(configuration.primary_color, 15));
        document.documentElement.style.setProperty('--primary-color-light', lightenColor(configuration.primary_color, 15));
      }
      
      if (configuration.secondary_color) {
        document.documentElement.style.setProperty('--secondary-color', configuration.secondary_color);
      }
      
      // Adicionar outras variáveis de cor padrão
      document.documentElement.style.setProperty('--success-color', '#28a745');
      document.documentElement.style.setProperty('--warning-color', '#ffc107');
      document.documentElement.style.setProperty('--danger-color', '#dc3545');
      document.documentElement.style.setProperty('--info-color', '#17a2b8');
      document.documentElement.style.setProperty('--light-color', '#f8f9fa');
      document.documentElement.style.setProperty('--dark-color', '#343a40');
      document.documentElement.style.setProperty('--text-muted', '#666');
      document.documentElement.style.setProperty('--border-color', '#e0e0e0');
      
      // Update document title and favicon
      document.title = configuration.app_name;
      
      // Update favicon - handle multiple favicon link types
      const faviconSelectors = [
        'link[rel="icon"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]'
      ];
      
      faviconSelectors.forEach(selector => {
        let link = document.querySelector(selector);
        if (link) {
          link.href = configuration.favicon_url;
        }
      });
      
      // Ensure at least one favicon link exists
      if (!document.querySelector('link[rel="icon"]')) {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = configuration.favicon_url;
        document.head.appendChild(favicon);
      }
      
      // Update theme colors
      const themeColorMeta = document.querySelector('meta[name="theme-color"]') || document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      themeColorMeta.content = configuration.pwa.theme_color;
      if (!document.querySelector('meta[name="theme-color"]')) {
        document.head.appendChild(themeColorMeta);
      }
      
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao carregar configuração do sistema');
      console.error('Error in forceUpdateSystemConfiguration:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

const getters = {
  systemConfiguration: state => state.configuration,
  appName: state => state.configuration.app_name,
  primaryColor: state => state.configuration.primary_color,
  secondaryColor: state => state.configuration.secondary_color,
  logoUrl: state => state.configuration.logo_url,
  logoTextUrl: state => state.configuration.logo_text_url,
  faviconUrl: state => state.configuration.favicon_url,
  pwaConfig: state => state.configuration.pwa,
  isLoading: state => state.loading,
  hasError: state => state.error !== null,
  error: state => state.error
};

// Métodos auxiliares para manipular cores
function darkenColor(hexColor, percentage) {
  hexColor = hexColor.replace('#', '');
  const rgb = hexColor.match(/.{2}/g).map(color => parseInt(color, 16));
  const darkened = rgb.map(color => Math.max(0, color - (color * percentage / 100)));
  return '#' + darkened.map(color => Math.round(color).toString(16).padStart(2, '0')).join('');
}

function lightenColor(hexColor, percentage) {
  hexColor = hexColor.replace('#', '');
  const rgb = hexColor.match(/.{2}/g).map(color => parseInt(color, 16));
  const lightened = rgb.map(color => Math.min(255, color + ((255 - color) * percentage / 100)));
  return '#' + lightened.map(color => Math.round(color).toString(16).padStart(2, '0')).join('');
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};